import { ReactNode, useContext, useEffect, useState } from 'react';
import { Can } from '@casl/react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AbilityContext from '@/contexts/AbilityContext';
import { Action } from '@/types/permissions';
import {
  CustomerReceivable,
  CustomerReceivableDetail
} from '@/types/CustomerReceivable.interface';
import { receivableService } from '@/data/receivableService';
import { formatCurrency } from '@/utils/formatters';
import {
  formatDateToDayMonthYear,
  formatDateToDayMonthYearTime
} from '@/utils/dateFormatters';
import { maskTaxId } from '@/utils/taxIdMask';
import {
  RECEIVABLE_STATUS_COLOR,
  canVoidReceivable,
  collectionPaymentLabelKey,
  isReversalCollection
} from '@/utils/receivables';

interface ReceivableDetailDialogProps {
  open: boolean;
  /** The listing row that was clicked (shown while the detail loads). */
  receivable: CustomerReceivable | null;
  onClose: () => void;
  /** Called after the receivable was cancelled so the page can refresh. */
  onVoided: () => void;
}

const SummaryItem = ({
  label,
  children
}: {
  label: string;
  children: ReactNode;
}) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body1" component="div" fontWeight="bold">
      {children}
    </Typography>
  </Box>
);

/**
 * Detail of one Açık Hesap receivable with its collection history. Collections
 * (Cari Tahsilat) are made at the POS, never here; the admin can only cancel a
 * receivable that has no collection yet.
 */
const ReceivableDetailDialog = ({
  open,
  receivable,
  onClose,
  onVoided
}: ReceivableDetailDialogProps) => {
  const { t } = useTranslation();
  const ability = useContext(AbilityContext);

  const [detail, setDetail] = useState<CustomerReceivableDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);

  const receivableId = receivable?.id;

  useEffect(() => {
    if (!open || receivableId === undefined) return;
    let cancelled = false;
    setDetail(null);
    setError(undefined);
    setVoidOpen(false);
    setVoidReason('');
    setLoading(true);
    receivableService
      .getById(receivableId)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch((err: any) => {
        if (!cancelled) {
          setError(
            err?.response?.data?.message || t('receivables.error.detail')
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, receivableId]);

  if (!receivable) return null;

  // The freshly loaded detail wins over the (possibly older) listing row. A
  // detail left over from a previously opened receivable is never shown.
  const loadedDetail = detail?.id === receivable.id ? detail : null;
  const current: CustomerReceivable = loadedDetail ?? receivable;
  const collections = loadedDetail?.collections ?? [];
  const voidable = canVoidReceivable(current);

  const handleVoid = async () => {
    try {
      setVoiding(true);
      await receivableService.voidReceivable(
        receivable.id,
        voidReason.trim() || undefined
      );
    } catch (err: any) {
      setError(err?.response?.data?.message || t('receivables.error.void'));
      setVoidOpen(false);
      return;
    } finally {
      setVoiding(false);
    }
    setVoidOpen(false);
    onVoided();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>{t('receivables.detail.title')}</DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <SummaryItem label={t('receivables.column.customer')}>
                {current.customer?.name}
              </SummaryItem>
            </Grid>
            <Grid item xs={12} sm={4}>
              <SummaryItem label={t('receivables.column.taxId')}>
                {maskTaxId(current.customer?.taxId)}
              </SummaryItem>
            </Grid>
            <Grid item xs={12} sm={4}>
              <SummaryItem label={t('receivables.column.status')}>
                <Chip
                  label={t(`receivables.status.${current.status}`)}
                  color={RECEIVABLE_STATUS_COLOR[current.status]}
                  size="small"
                />
              </SummaryItem>
            </Grid>
            <Grid item xs={12} sm={4}>
              <SummaryItem label={t('receivables.column.documentNo')}>
                {current.documentNo}
              </SummaryItem>
            </Grid>
            <Grid item xs={12} sm={4}>
              <SummaryItem label={t('receivables.column.date')}>
                {formatDateToDayMonthYear(
                  current.documentDate ?? current.createdAt
                )}
              </SummaryItem>
            </Grid>
            <Grid item xs={12} sm={4} />
            <Grid item xs={12} sm={4}>
              <SummaryItem label={t('receivables.column.amount')}>
                {formatCurrency(Number(current.amount))}
              </SummaryItem>
            </Grid>
            <Grid item xs={12} sm={4}>
              <SummaryItem label={t('receivables.column.paidAmount')}>
                {formatCurrency(Number(current.paidAmount))}
              </SummaryItem>
            </Grid>
            <Grid item xs={12} sm={4}>
              <SummaryItem label={t('receivables.column.remaining')}>
                {formatCurrency(Number(current.remaining))}
              </SummaryItem>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            {t('receivables.collections.title')}
          </Typography>

          {loading ? (
            <Box p={2} display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          ) : collections.length === 0 ? (
            !error && (
              <Typography color="text.secondary">
                {t('receivables.collections.empty')}
              </Typography>
            )
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    {t('receivables.collections.paymentType')}
                  </TableCell>
                  <TableCell align="right">
                    {t('receivables.collections.amount')}
                  </TableCell>
                  <TableCell>{t('receivables.collections.date')}</TableCell>
                  <TableCell>{t('receivables.collections.employee')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {collections.map((collection) => {
                  const reversal = isReversalCollection(collection);
                  return (
                    <TableRow key={collection.id}>
                      <TableCell>
                        {t(
                          collectionPaymentLabelKey(collection.paymentType),
                          collection.paymentType
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          color={reversal ? 'error' : 'text.primary'}
                          fontWeight="bold"
                        >
                          {formatCurrency(Number(collection.amount))}
                        </Typography>
                        {reversal && (
                          <Typography
                            variant="caption"
                            color="error"
                            display="block"
                          >
                            {t('receivables.collections.reversed')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {formatDateToDayMonthYearTime(collection.createdAt)}
                      </TableCell>
                      <TableCell>
                        {[
                          collection.employee?.name,
                          collection.employee?.surname
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Can I={Action.Update} a="CustomerReceivable" ability={ability}>
            <Box sx={{ mr: 'auto', pl: 1 }}>
              <Button
                color="error"
                variant="outlined"
                disabled={!voidable || loading}
                onClick={() => setVoidOpen(true)}
              >
                {t('receivables.void.action')}
              </Button>
              {!voidable && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  {t('receivables.void.disabledHint')}
                </Typography>
              )}
            </Box>
          </Can>
          <Button onClick={onClose}>{t('close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={voidOpen}
        onClose={voiding ? undefined : () => setVoidOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t('receivables.void.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('receivables.void.confirm', {
              name: current.customer?.name
            })}
          </DialogContentText>
          <TextField
            label={t('receivables.void.reason')}
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
            fullWidth
            multiline
            rows={2}
            inputProps={{ maxLength: 250 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoidOpen(false)} disabled={voiding}>
            {t('cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleVoid}
            disabled={voiding}
          >
            {t('receivables.void.action')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReceivableDetailDialog;
